import { GAMMA_DESK_ID } from '@/api/chat/constants';
import type { UserRole } from '@/stores/slices/selected-user-config';

import type { ResolvedSendAsIdentity, SendAsIdentity, SendAsKind, ThreadSummary } from '../types';

export interface SendAsInput {
  role: UserRole;
  userId?: string;
  userName?: string | null;
  entityId?: string | null;
  entityName?: string | null;
}

export const deriveSendAsIdentity = ({
  role,
  userId,
  userName,
  entityId,
  entityName,
}: SendAsInput): SendAsIdentity => {
  if (role === 'admin') {
    return {
      kind: 'gammaDesk',
      participantId: GAMMA_DESK_ID,
      label: 'Gamma Prime',
      isDesk: true,
    };
  }

  if (role === 'entity' && entityId) {
    return {
      kind: 'entityDesk',
      participantId: entityId,
      label: entityName?.trim() || 'Entity',
      isDesk: true,
    };
  }

  return {
    kind: 'individual',
    participantId: userId,
    label: userName?.trim() || 'You',
    isDesk: false,
  };
};

const SEND_AS_PHRASE: Record<SendAsKind, string> = {
  individual: 'your individual account',
  gammaDesk: 'the Gamma Prime account',
  entityDesk: 'your entity account',
};

export interface WriteBlock {
  requiredIdentity: SendAsKind;
  reason: string;
}

export const writeBlockFor = (
  identity: SendAsIdentity,
  thread: Pick<ThreadSummary, 'ownIdentity'>,
): WriteBlock | null => {
  // Checks the property rather than calling `isResolvedIdentity`: the negative branch of an
  // `x is A & B` predicate narrows to `never` for the supertype, which would lose
  // `identity.kind` below.
  if (!identity.participantId) {
    return {
      requiredIdentity: identity.kind,
      reason: 'Loading your account — one moment.',
    };
  }

  if (thread.ownIdentity === identity.kind) return null;

  return {
    requiredIdentity: thread.ownIdentity,
    reason: `This conversation belongs to ${SEND_AS_PHRASE[thread.ownIdentity]}. Switch account in the top-right menu to reply.`,
  };
};

export const isResolvedIdentity = (identity: SendAsIdentity): identity is ResolvedSendAsIdentity =>
  Boolean(identity.participantId);

export const threadsForIdentity = (threads: ThreadSummary[], kind: SendAsKind): ThreadSummary[] =>
  threads.filter((thread) => thread.ownIdentity === kind);
